import type { CollectionSlug } from '@/features/metadata-library/types/metadataLibrary';
export type { CollectionSlug } from '@/features/metadata-library/types/metadataLibrary';
import type { DialogId } from '@/shared/lib/dialogRegistry';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

/**
 * The global metadata libraries, as their URL path segments.
 * These are the `{collection}` in `/{collection}`
 * *and* the `$collection` route param, so one tuple serves the transport, the
 * tab bar, and route-param validation.
 */
export const COLLECTION_SLUGS = ['environments', 'operators'] as const;

/** Narrows an untrusted route param (or any string) to a known collection slug. */
export function isCollectionSlug(value: string): value is CollectionSlug {
  return (COLLECTION_SLUGS as readonly string[]).includes(value);
}

/** The collection shown when no tab is specified. */
export const DEFAULT_COLLECTION: CollectionSlug = 'environments';

/**
 * Narrows the `$collection` route param for a route whose `beforeLoad` has
 * already redirected an unknown slug away. The fallback is unreachable in
 * practice but keeps the param typed without a cast — and if a future route ever
 * forgets the guard, it lands on the default tab rather than requesting an
 * unknown collection.
 */
export function collectionFromParam(value: string): CollectionSlug {
  return isCollectionSlug(value) ? value : DEFAULT_COLLECTION;
}

/**
 * What the collection-generic surfaces need to know about each library: its name,
 * its id field, and its list columns.
 *
 * Deliberately **not** the form's shape. Each library's fields, ordering, controls
 * and request bodies live in its own `lib/<collection>Form.ts` and
 * `components/<Collection>RecordFields.tsx`, because the forms differ in ways a
 * shared table cannot express. What remains here is genuinely uniform: one list view,
 * one table, one row menu, one set of labels.
 */
export interface CollectionConfig {
  /** Tab label / list heading (plural). */
  readonly label: MessageDescriptor;
  /** Used in prose about a single record ("Delete this operator?"). */
  readonly singular: MessageDescriptor;
  /** The record's read-only id field (`environmentId`, `operatorId`, …). */
  readonly idFieldKey: string;
  /** Business fields shown as list columns, in order, after the id column. */
  readonly columnKeys: readonly string[];
  /** Business fields shown in the compact mobile summary, in display order. */
  readonly compactColumnKeys: readonly string[];
}

export const COLLECTIONS: Record<CollectionSlug, CollectionConfig> = {
  environments: {
    label: msg`Venues`,
    singular: msg`venue`,
    idFieldKey: 'environmentId',
    columnKeys: [
      'organizationName',
      'opsEnvironmentId',
      'businessName',
      'environmentL1',
      'environmentL2',
      'city',
      'country',
    ],
    compactColumnKeys: ['organizationName', 'businessName', 'environmentL1', 'environmentL2'],
  },
  operators: {
    label: msg`Operators`,
    singular: msg`operator`,
    idFieldKey: 'operatorId',
    columnKeys: [
      'organizationName',
      'vendorOperatorId',
      'operatorName',
      'operatorJob',
      'vendor',
      'consent',
    ],
    compactColumnKeys: ['organizationName', 'operatorName', 'operatorJob', 'vendor'],
  },
};

/**
 * Which dialog opens a create or edit form, per collection.
 *
 * The libraries have separate dialogs, but the list view, its table and its
 * row menu are one implementation over a runtime `$collection` param — this closed,
 * exhaustive table is how they reach the right one. It is a compile-time mapping over
 * known slugs, not a runtime decision about a field's shape.
 */
export const COLLECTION_DIALOG_IDS: Record<
  CollectionSlug,
  { readonly create: DialogId; readonly edit: DialogId }
> = {
  environments: { create: 'create-venue', edit: 'edit-venue' },
  operators: { create: 'create-operator', edit: 'edit-operator' },
};

/**
 * The object-valued field and its three required properties. All three must be
 * present or the whole key omitted/`null`. The runtime field schema remains
 * authoritative for which properties are required and how they are validated.
 */
export const HAND_SIZE_PROPERTY_KEYS = [
  'wristCenterToMiddleFingertipCm',
  'thumbToPinkySpreadCm',
  'middleFingertipToKnuckleCm',
] as const;
export type HandSizePropertyKey = (typeof HAND_SIZE_PROPERTY_KEYS)[number];
